import React, { useState, useEffect, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import { motion, AnimatePresence } from 'framer-motion';
import { ArrowLeft, Plus, Edit2, Trash2, Check, X, ChevronDown, ChevronRight, Settings, Download, Loader2, Upload, Trash2 as TrashIcon } from 'lucide-react';
import Navbar from '../components/layout/Navbar';
import Button from '../components/ui/Button';
import Input from '../components/ui/Input';
import { Select, SelectTrigger, SelectContent, SelectItem, SelectValue } from '../components/ui/Select';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '../components/ui/Dialog';
import { loadAssets, loadAssetTypes, saveAssetTypes } from '../utils/storage';

const Admin = () => {
  const navigate = useNavigate();
  const [assetTypes, setAssetTypes] = useState([]);
  const [assets, setAssets] = useState([]);
  const [showAddForm, setShowAddForm] = useState(false);
  const [newTypeName, setNewTypeName] = useState('');
  const [editingId, setEditingId] = useState(null);
  const [editingValue, setEditingValue] = useState('');
  const [expandedTypes, setExpandedTypes] = useState(new Set());
  const [deleteDialog, setDeleteDialog] = useState({ open: false, type: null });
  const [editingAttribute, setEditingAttribute] = useState({ typeValue: null, attrIndex: null, attr: null });
  const [addingAttribute, setAddingAttribute] = useState({ typeValue: null, open: false });
  const [backingUp, setBackingUp] = useState(false);
  const [backgroundImage, setBackgroundImage] = useState(null);
  const [opacity, setOpacity] = useState(0.3);
  const [uploadingBg, setUploadingBg] = useState(false);
  const [bgSettingsExpanded, setBgSettingsExpanded] = useState(false);
  const fileInputRef = useRef(null);
  
  // Get full URL for background image preview
  const API_BASE = process.env.REACT_APP_API_BASE || 'http://localhost:4000';
  const backgroundImageUrl = backgroundImage 
    ? (backgroundImage.startsWith('http') ? backgroundImage : `${API_BASE}${backgroundImage}`)
    : null;

  useEffect(() => {
    const fetchData = async () => {
      const [assetTypesData, assetsData] = await Promise.all([
        loadAssetTypes(),
        loadAssets()
      ]);
      setAssetTypes(assetTypesData);
      setAssets(assetsData);
    };
    fetchData();
  }, []);

  useEffect(() => {
    // Load admin settings (background image and opacity)
    const loadAdminSettings = async () => {
      try {
        const response = await fetch('/api/admin/settings');
        if (response.ok) {
          const settings = await response.json();
          setBackgroundImage(settings.backgroundImage || null);
          setOpacity(settings.opacity !== undefined ? settings.opacity : 0.3);
        }
      } catch (error) {
        console.error('Error loading admin settings:', error);
      }
    };
    loadAdminSettings();
  }, []);

  const getAssetCount = (typeValue) => {
    return assets.filter(a => a.type === typeValue).length;
  };

  const toggleType = (typeValue) => {
    setExpandedTypes(prev => {
      const next = new Set(prev);
      if (next.has(typeValue)) {
        next.delete(typeValue);
      } else {
        next.add(typeValue);
      }
      return next;
    });
  };

  const handleAddType = async () => {
    if (!newTypeName.trim()) {
      alert('Please enter a type name');
      return;
    }
    const value = newTypeName.toLowerCase().replace(/\s+/g, '_');
    if (assetTypes.find(t => t.value === value)) {
      alert('This type already exists');
      return;
    }
    const newType = {
      value,
      label: newTypeName.trim(),
      attributes: [
        { name: 'name', label: 'Asset Name', type: 'text', required: true },
        { name: 'purchaseDate', label: 'Purchase Date', type: 'date', required: true }
      ]
    };
    const updatedTypes = [...assetTypes, newType];
    try {
      await saveAssetTypes(updatedTypes);
      setAssetTypes(updatedTypes);
      setNewTypeName('');
      setShowAddForm(false);
    } catch (error) {
      alert(`Failed to save asset type: ${error.message}`);
      console.error('Error saving asset type:', error);
    }
  };

  const handleEdit = (type) => {
    setEditingId(type.value);
    setEditingValue(type.label);
  };

  const handleSaveEdit = async () => {
    if (!editingValue.trim()) {
      alert('Name cannot be empty');
      return;
    }
    const updatedTypes = assetTypes.map(t =>
      t.value === editingId ? { ...t, label: editingValue.trim() } : t
    );
    try {
      await saveAssetTypes(updatedTypes);
      setAssetTypes(updatedTypes);
      setEditingId(null);
      setEditingValue('');
    } catch (error) {
      alert(`Failed to save changes: ${error.message}`);
      console.error('Error saving asset type:', error);
    }
  };

  const handleCancelEdit = () => {
    setEditingId(null);
    setEditingValue('');
  };

  const handleDeleteClick = (type) => {
    const count = getAssetCount(type.value);
    if (count > 0) {
      alert(`Cannot delete this type. ${count} asset(s) are using it.`);
      return;
    }
    setDeleteDialog({ open: true, type });
  };

  const handleDeleteConfirm = async () => {
    const updatedTypes = assetTypes.filter(t => t.value !== deleteDialog.type.value);
    try {
      await saveAssetTypes(updatedTypes);
      setAssetTypes(updatedTypes);
      setDeleteDialog({ open: false, type: null });
    } catch (error) {
      alert(`Failed to delete asset type: ${error.message}`);
      console.error('Error deleting asset type:', error);
    }
  };

  const handleStartAddAttribute = (typeValue) => {
    setAddingAttribute({ typeValue, open: true });
  };

  const handleAddAttribute = async (attrData) => {
    const { label, name, type, required } = attrData;
    const typeIndex = assetTypes.findIndex(t => t.value === addingAttribute.typeValue);
    if (typeIndex === -1) return;

    const updatedTypes = [...assetTypes];
    updatedTypes[typeIndex] = {
      ...updatedTypes[typeIndex],
      attributes: [...updatedTypes[typeIndex].attributes, { name, label, type, required }]
    };
    try {
      await saveAssetTypes(updatedTypes);
      setAssetTypes(updatedTypes);
      setAddingAttribute({ typeValue: null, open: false });
    } catch (error) {
      alert(`Failed to save attribute: ${error.message}`);
      console.error('Error saving attribute:', error);
    }
  };

  const handleStartEditAttribute = (typeValue, attrIndex) => {
    const type = assetTypes.find(t => t.value === typeValue);
    if (!type || !type.attributes[attrIndex]) return;
    setEditingAttribute({ typeValue, attrIndex, attr: { ...type.attributes[attrIndex] } });
  };

  const handleSaveAttribute = async (attrData) => {
    const typeIndex = assetTypes.findIndex(t => t.value === editingAttribute.typeValue);
    if (typeIndex === -1) return;

    const updatedTypes = [...assetTypes];
    updatedTypes[typeIndex] = {
      ...updatedTypes[typeIndex],
      attributes: updatedTypes[typeIndex].attributes.map((attr, idx) =>
        idx === editingAttribute.attrIndex ? { ...attrData } : attr
      )
    };
    try {
      await saveAssetTypes(updatedTypes);
      setAssetTypes(updatedTypes);
      setEditingAttribute({ typeValue: null, attrIndex: null, attr: null });
    } catch (error) {
      alert(`Failed to save attribute changes: ${error.message}`);
      console.error('Error saving attribute:', error);
    }
  };

  const handleDeleteAttribute = async (typeValue, attrIndex) => {
    const typeIndex = assetTypes.findIndex(t => t.value === typeValue);
    if (typeIndex === -1) return;

    const attr = assetTypes[typeIndex].attributes[attrIndex];
    // Prevent deleting required 'name' attribute
    if (attr.name === 'name') {
      alert('Cannot delete the "name" attribute as it is required for all assets.');
      return;
    }

    const updatedTypes = [...assetTypes];
    updatedTypes[typeIndex] = {
      ...updatedTypes[typeIndex],
      attributes: updatedTypes[typeIndex].attributes.filter((_, idx) => idx !== attrIndex)
    };
    try {
      await saveAssetTypes(updatedTypes);
      setAssetTypes(updatedTypes);
    } catch (error) {
      alert(`Failed to delete attribute: ${error.message}`);
      console.error('Error deleting attribute:', error);
    }
  };

  const handleBackup = async () => {
    setBackingUp(true);
    try {
      const response = await fetch('/api/backup', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
      });

      if (!response.ok) {
        const error = await response.json();
        throw new Error(error.error || 'Failed to create backup');
      }

      const result = await response.json();
      alert(`Backup created successfully!\n\nFilename: ${result.filename}\nSize: ${(result.size / 1024 / 1024).toFixed(2)} MB\n\nThe backup file is located in the data/backup directory.`);
    } catch (error) {
      alert(`Failed to create backup: ${error.message}`);
      console.error('Error creating backup:', error);
    } finally {
      setBackingUp(false);
    }
  };

  const handleBackgroundUpload = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;

    if (!file.type.startsWith('image/')) {
      alert('Please select an image file');
      return;
    }

    setUploadingBg(true);
    const formData = new FormData();
    formData.append('image', file);

    try {
      const response = await fetch('/api/admin/background/upload', {
        method: 'POST',
        body: formData,
      });

      if (!response.ok) {
        const error = await response.json();
        throw new Error(error.error || 'Failed to upload background image');
      }

      const result = await response.json();
      setBackgroundImage(result.path);
      // Opacity is preserved from settings
    } catch (error) {
      alert(`Failed to upload background image: ${error.message}`);
      console.error('Error uploading background:', error);
    } finally {
      setUploadingBg(false);
      // Reset input
      e.target.value = '';
    }
  };

  const handleOpacityChange = async (newOpacity) => {
    setOpacity(newOpacity);
    try {
      await fetch('/api/admin/settings', {
        method: 'PUT',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({
          opacity: newOpacity,
          backgroundImage: backgroundImage,
        }),
      });
    } catch (error) {
      console.error('Error updating opacity:', error);
    }
  };

  const handleRemoveBackground = async () => {
    try {
      const response = await fetch('/api/admin/background', {
        method: 'DELETE',
      });

      if (!response.ok) {
        const error = await response.json();
        throw new Error(error.error || 'Failed to remove background image');
      }

      setBackgroundImage(null);
    } catch (error) {
      alert(`Failed to remove background image: ${error.message}`);
      console.error('Error removing background:', error);
    }
  };

  return (
    <>
      <Navbar />
      <div className="pt-24 pb-12 px-4 sm:px-6 lg:px-8 max-w-4xl mx-auto">
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.5 }}
        >
          <Button
            variant="ghost"
            onClick={() => navigate('/dashboard')}
            className="mb-6 flex items-center space-x-2"
          >
            <ArrowLeft className="h-4 w-4" />
            <span>Back to Dashboard</span>
          </Button>

          {/* Background Image Settings Section */}
          <div className="glassmorphic p-6 sm:p-8 mb-6">
            <div className="flex justify-between items-center mb-4">
              <div>
                <h2 className="text-xl font-light text-foreground mb-1">Background Image</h2>
                <p className="text-sm text-body">Customize the admin section background</p>
              </div>
              <Button
                onClick={() => setBgSettingsExpanded(!bgSettingsExpanded)}
                variant="ghost"
                size="sm"
              >
                {bgSettingsExpanded ? <ChevronDown className="h-4 w-4" /> : <ChevronRight className="h-4 w-4" />}
              </Button>
            </div>

            <AnimatePresence>
              {bgSettingsExpanded && (
                <motion.div
                  initial={{ height: 0, opacity: 0 }}
                  animate={{ height: 'auto', opacity: 1 }}
                  exit={{ height: 0, opacity: 0 }}
                  transition={{ duration: 0.2 }}
                  className="overflow-hidden"
                >
                  <div className="space-y-4 pt-4 border-t border-white/10">
                    {/* Current Background Preview */}
                    {backgroundImage && (
                      <div className="relative">
                        <div className="relative w-full h-48 rounded-lg overflow-hidden border border-white/10">
                          <img
                            src={backgroundImageUrl}
                            alt="Background preview"
                            className="w-full h-full object-cover"
                          />
                          <div 
                            className="absolute inset-0 flex items-center justify-center"
                            style={{
                              backgroundColor: `rgba(0, 0, 0, ${1 - opacity})`,
                            }}
                          >
                            <span className="text-white text-sm">Preview (visibility: {Math.round(opacity * 100)}%)</span>
                          </div>
                        </div>
                        <Button
                          onClick={handleRemoveBackground}
                          variant="ghost"
                          size="sm"
                          className="absolute top-2 right-2 text-destructive hover:bg-destructive/10 z-10"
                        >
                          <TrashIcon className="h-4 w-4" />
                        </Button>
                      </div>
                    )}

                    {/* Upload Button */}
                    <div>
                      <label className="block text-sm font-medium mb-2">
                        {backgroundImage ? 'Change Background Image' : 'Upload Background Image'}
                      </label>
                      <div className="flex items-center space-x-2">
                        <input
                          ref={fileInputRef}
                          type="file"
                          accept="image/*"
                          onChange={handleBackgroundUpload}
                          className="hidden"
                          disabled={uploadingBg}
                        />
                        <Button
                          onClick={() => fileInputRef.current?.click()}
                          variant="outline"
                          disabled={uploadingBg}
                          className="flex items-center space-x-2"
                        >
                          {uploadingBg ? (
                            <>
                              <Loader2 className="h-4 w-4 animate-spin" />
                              <span>Uploading...</span>
                            </>
                          ) : (
                            <>
                              <Upload className="h-4 w-4" />
                              <span>Choose Image</span>
                            </>
                          )}
                        </Button>
                      </div>
                      <p className="text-xs text-muted-foreground mt-1">
                        Supported formats: JPG, PNG, GIF (max 10MB)
                      </p>
                    </div>

                    {/* Opacity Slider */}
                    <div>
                      <label className="block text-sm font-medium mb-2">
                        Transparency: {Math.round(opacity * 100)}%
                      </label>
                      <div className="flex items-center space-x-3">
                        <input
                          type="range"
                          min="0"
                          max="1"
                          step="0.01"
                          value={opacity}
                          onChange={(e) => handleOpacityChange(parseFloat(e.target.value))}
                          className="flex-1 h-2 bg-white/10 rounded-lg appearance-none cursor-pointer slider"
                          style={{
                            background: `linear-gradient(to right, rgba(255,255,255,0.3) 0%, rgba(255,255,255,0.3) ${opacity * 100}%, rgba(255,255,255,0.1) ${opacity * 100}%, rgba(255,255,255,0.1) 100%)`
                          }}
                        />
                        <span className="text-sm text-muted-foreground w-12 text-right">
                          {Math.round(opacity * 100)}%
                        </span>
                      </div>
                      <p className="text-xs text-muted-foreground mt-1">
                        Adjust how visible the background image is (0% = invisible, 100% = fully visible)
                      </p>
                    </div>
                  </div>
                </motion.div>
              )}
            </AnimatePresence>
          </div>

          <div className="glassmorphic p-6 sm:p-8">
            <div className="mb-6 flex justify-between items-start">
              <div>
                <h1 className="text-2xl font-light text-foreground mb-2">Admin Settings</h1>
                <p className="text-body">Manage asset types and their attributes</p>
              </div>
              <Button
                onClick={handleBackup}
                disabled={backingUp}
                className="flex items-center space-x-2"
                variant="outline"
              >
                {backingUp ? (
                  <>
                    <Loader2 className="h-4 w-4 animate-spin" />
                    <span>Creating Backup...</span>
                  </>
                ) : (
                  <>
                    <Download className="h-4 w-4" />
                    <span>Create Backup</span>
                  </>
                )}
              </Button>
            </div>

            <div className="flex justify-between items-center mb-4">
              <h3 className="text-lg font-medium text-foreground">Asset Types</h3>
              <Button
                onClick={() => setShowAddForm(!showAddForm)}
                className="flex items-center space-x-2"
                size="sm"
              >
                {showAddForm ? <X className="h-4 w-4" /> : <Plus className="h-4 w-4" />}
                <span>{showAddForm ? 'Cancel' : 'Add Type'}</span>
              </Button>
            </div>

            <AnimatePresence>
              {showAddForm && (
                <motion.div
                  initial={{ height: 0, opacity: 0 }}
                  animate={{ height: 'auto', opacity: 1 }}
                  exit={{ height: 0, opacity: 0 }}
                  transition={{ duration: 0.2 }}
                  className="overflow-hidden mb-4"
                >
                  <div className="flex space-x-2 p-4 bg-white/5 rounded-lg border border-white/10">
                    <Input
                      value={newTypeName}
                      onChange={(e) => setNewTypeName(e.target.value)}
                      placeholder="e.g., Electronics"
                      onKeyPress={(e) => e.key === 'Enter' && handleAddType()}
                      autoFocus
                    />
                    <Button onClick={handleAddType}>Add</Button>
                  </div>
                </motion.div>
              )}
            </AnimatePresence>

            <div className="space-y-2">
              {assetTypes.map((type, index) => {
                const isExpanded = expandedTypes.has(type.value);
                const isEditing = editingId === type.value;

                return (
                  <motion.div
                    key={type.value}
                    initial={{ opacity: 0, x: -10 }}
                    animate={{ opacity: 1, x: 0 }}
                    transition={{ delay: index * 0.05 }}
                    className="border border-white/10 rounded-lg overflow-hidden"
                  >
                    <div className="flex items-center justify-between p-4 bg-white/5">
                      {isEditing ? (
                        <div className="flex items-center space-x-2 flex-1">
                          <Input
                            value={editingValue}
                            onChange={(e) => setEditingValue(e.target.value)}
                            onKeyPress={(e) => {
                              if (e.key === 'Enter') handleSaveEdit();
                              if (e.key === 'Escape') handleCancelEdit();
                            }}
                            autoFocus
                          />
                          <Button size="sm" onClick={handleSaveEdit}>
                            <Check className="h-4 w-4" />
                          </Button>
                          <Button size="sm" variant="ghost" onClick={handleCancelEdit}>
                            <X className="h-4 w-4" />
                          </Button>
                        </div>
                      ) : (
                        <>
                          <div className="flex items-center space-x-3 flex-1">
                            <button
                              onClick={() => toggleType(type.value)}
                              className="flex items-center space-x-2 hover:opacity-80 transition-opacity"
                            >
                              {isExpanded ? (
                                <ChevronDown className="h-4 w-4 text-muted-foreground" />
                              ) : (
                                <ChevronRight className="h-4 w-4 text-muted-foreground" />
                              )}
                              <span className="text-foreground font-medium">{type.label}</span>
                              <span className="text-xs text-muted-foreground">
                                ({getAssetCount(type.value)} asset{getAssetCount(type.value) !== 1 ? 's' : ''})
                              </span>
                              <span className="text-xs text-muted-foreground">
                                • {type.attributes?.length || 0} attribute{type.attributes?.length !== 1 ? 's' : ''}
                              </span>
                            </button>
                          </div>
                          <div className="flex space-x-2">
                            <Button
                              size="sm"
                              variant="ghost"
                              onClick={() => toggleType(type.value)}
                              title="Manage Attributes"
                            >
                              <Settings className="h-4 w-4" />
                            </Button>
                            <Button
                              size="sm"
                              variant="ghost"
                              onClick={() => handleEdit(type)}
                            >
                              <Edit2 className="h-4 w-4" />
                            </Button>
                            <Button
                              size="sm"
                              variant="ghost"
                              onClick={() => handleDeleteClick(type)}
                              className="text-destructive hover:bg-destructive/10"
                            >
                              <Trash2 className="h-4 w-4" />
                            </Button>
                          </div>
                        </>
                      )}
                    </div>

                    <AnimatePresence>
                      {isExpanded && !isEditing && (
                        <motion.div
                          initial={{ height: 0, opacity: 0 }}
                          animate={{ height: 'auto', opacity: 1 }}
                          exit={{ height: 0, opacity: 0 }}
                          transition={{ duration: 0.2 }}
                          className="overflow-hidden"
                        >
                          <div className="p-4 bg-white/2 border-t border-white/10">
                            <div className="flex justify-between items-center mb-3">
                              <h4 className="text-sm font-medium text-foreground">Attributes</h4>
                              <Button
                                size="sm"
                                onClick={() => handleStartAddAttribute(type.value)}
                                className="flex items-center space-x-1"
                              >
                                <Plus className="h-3 w-3" />
                                <span>Add Attribute</span>
                              </Button>
                            </div>
                            <div className="space-y-2">
                              {type.attributes?.map((attr, attrIndex) => (
                                <div
                                  key={attrIndex}
                                  className="flex items-center justify-between p-2 bg-white/5 rounded border border-white/10"
                                >
                                  <div className="flex items-center space-x-3 flex-1">
                                    <span className="text-sm text-foreground font-medium">{attr.label}</span>
                                    <span className="text-xs text-muted-foreground">({attr.type})</span>
                                    {attr.required && (
                                      <span className="text-xs text-primary">Required</span>
                                    )}
                                  </div>
                                  <div className="flex space-x-1">
                                    <Button
                                      size="sm"
                                      variant="ghost"
                                      onClick={() => handleStartEditAttribute(type.value, attrIndex)}
                                    >
                                      <Edit2 className="h-3 w-3" />
                                    </Button>
                                    <Button
                                      size="sm"
                                      variant="ghost"
                                      onClick={() => handleDeleteAttribute(type.value, attrIndex)}
                                      className="text-destructive hover:bg-destructive/10"
                                      disabled={attr.name === 'name'}
                                    >
                                      <Trash2 className="h-3 w-3" />
                                    </Button>
                                  </div>
                                </div>
                              ))}
                            </div>
                          </div>
                        </motion.div>
                      )}
                    </AnimatePresence>
                  </motion.div>
                );
              })}
            </div>
          </div>
        </motion.div>
      </div>

      {/* Delete Type Dialog */}
      <Dialog open={deleteDialog.open} onOpenChange={(open) => setDeleteDialog({ open, type: null })}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Delete Asset Type</DialogTitle>
            <DialogDescription>
              Are you sure you want to delete "{deleteDialog.type?.label}"? This action cannot be undone.
            </DialogDescription>
          </DialogHeader>
          <DialogFooter>
            <Button variant="outline" onClick={() => setDeleteDialog({ open: false, type: null })}>
              Cancel
            </Button>
            <Button variant="destructive" onClick={handleDeleteConfirm}>
              Delete
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Add Attribute Dialog */}
      <AttributeDialog
        open={addingAttribute.open}
        onClose={() => setAddingAttribute({ typeValue: null, open: false })}
        onSave={handleAddAttribute}
        mode="add"
      />

      {/* Edit Attribute Dialog */}
      <AttributeDialog
        open={editingAttribute.attr !== null}
        onClose={() => setEditingAttribute({ typeValue: null, attrIndex: null, attr: null })}
        onSave={handleSaveAttribute}
        mode="edit"
        attribute={editingAttribute.attr}
      />
    </>
  );
};

// Attribute Dialog Component
const AttributeDialog = ({ open, onClose, onSave, mode, attribute = null }) => {
  const [formData, setFormData] = useState({
    name: '',
    label: '',
    type: 'text',
    required: false
  });

  useEffect(() => {
    if (open) {
      if (mode === 'edit' && attribute) {
        setFormData({
          name: attribute.name || '',
          label: attribute.label || '',
          type: attribute.type || 'text',
          required: attribute.required || false
        });
      } else {
        setFormData({
          name: '',
          label: '',
          type: 'text',
          required: false
        });
      }
    }
  }, [open, mode, attribute]);

  const handleSubmit = (e) => {
    e.preventDefault();
    if (!formData.name.trim() || !formData.label.trim()) {
      alert('Name and Label are required');
      return;
    }
    // Convert name to camelCase for consistency
    const nameValue = formData.name.trim().replace(/\s+/g, '').replace(/^[A-Z]/, (match) => match.toLowerCase());
    onSave({
      name: nameValue,
      label: formData.label.trim(),
      type: formData.type,
      required: formData.required
    });
  };

  return (
    <Dialog open={open} onOpenChange={onClose}>
      <DialogContent>
        <DialogHeader>
          <DialogTitle>{mode === 'add' ? 'Add Attribute' : 'Edit Attribute'}</DialogTitle>
          <DialogDescription>
            {mode === 'add' 
              ? 'Add a new attribute to this asset type'
              : 'Modify the attribute details'}
          </DialogDescription>
        </DialogHeader>
        <form onSubmit={handleSubmit}>
          <div className="space-y-4 py-4">
            <div>
              <label className="block text-sm font-medium mb-2">
                Attribute Name <span className="text-primary">*</span>
              </label>
              <Input
                value={formData.name}
                onChange={(e) => setFormData(prev => ({ ...prev, name: e.target.value }))}
                placeholder="e.g., vin, make, year"
                required
                disabled={mode === 'edit' && attribute?.name === 'name'}
              />
              <p className="text-xs text-muted-foreground mt-1">
                Internal field name (camelCase, e.g., vin, make, year)
              </p>
            </div>
            <div>
              <label className="block text-sm font-medium mb-2">
                Display Label <span className="text-primary">*</span>
              </label>
              <Input
                value={formData.label}
                onChange={(e) => setFormData(prev => ({ ...prev, label: e.target.value }))}
                placeholder="e.g., VIN, Make, Year"
                required
              />
            </div>
            <div>
              <label className="block text-sm font-medium mb-2">
                Field Type <span className="text-primary">*</span>
              </label>
              <Select
                value={formData.type}
                onValueChange={(value) => setFormData(prev => ({ ...prev, type: value }))}
              >
                <SelectTrigger>
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="text">Text</SelectItem>
                  <SelectItem value="number">Number</SelectItem>
                  <SelectItem value="date">Date</SelectItem>
                  <SelectItem value="textarea">Textarea</SelectItem>
                </SelectContent>
              </Select>
            </div>
            <div className="flex items-center space-x-2">
              <input
                type="checkbox"
                id="required"
                checked={formData.required}
                onChange={(e) => setFormData(prev => ({ ...prev, required: e.target.checked }))}
                className="rounded border-input"
              />
              <label htmlFor="required" className="text-sm font-medium">
                Required field
              </label>
            </div>
          </div>
          <DialogFooter>
            <Button type="button" variant="outline" onClick={onClose}>
              Cancel
            </Button>
            <Button type="submit">
              {mode === 'add' ? 'Add' : 'Save'}
            </Button>
          </DialogFooter>
        </form>
      </DialogContent>
    </Dialog>
  );
};

export default Admin;